import React, { useContext, useEffect, useMemo, useState } from 'react';
import { Button, Form, Spinner, Table as BootstrapTable } from 'react-bootstrap';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Select from 'react-select';
import { resourceCache, useAsyncResource } from 'use-async-resource';
import { NewRequestContext } from '../../context/NewRequestContext';
import { NewRequestBranch } from '../../models/dashboard-models';
import {
    setBranchActive,
    setBranchInactive,
    saveClient,
    useGetNewRequestBranchesByClient,
    UseNewRequestAddBranch,
} from '../../utils/clients-hooks';
import { NewRequestTableRow } from './NewRequestTableRow';
import { User } from '../../models/table-models';
import { optionSelectStyles } from '../../styles/selectStyles';
import { toast } from 'react-toastify';
import { CustomToastContent } from '../CustomToastContent';
import { toastOptionsTopRight } from '../../utils/Enums/ToastOptions';
import { AuthContext } from '../../context/AuthContext';
import { Role } from '../../utils/Enums/Roles';
import { fetchBranchImage, fetchMapBranches } from '../../utils/dashboard-hooks';
import { BranchesContext } from '../../context/BranchesContext';
import { NewRequestTableButtons } from './NewRequestTableButtons';
import { useGetUsers } from '../../utils/users-hooks';

export const NewRequestBranchTable = ({
    setIsNewMarkerVissible,
}: {
    setIsNewMarkerVissible?: React.Dispatch<React.SetStateAction<boolean>>;
}) => {
    const { t } = useTranslation('translation', { keyPrefix: 'newRequest' });
    const { hasPermission, group } = useContext(AuthContext);
    const { getNewBranches, addBranchLocal, updateBranchLocal } = useContext(BranchesContext);
    const { selectedBranch, updateBranchAfterSave, updateClientAfterSave, onBranchSelect, selectedClient, getBranches } =
        useContext(NewRequestContext);
    const [editable, setEditable] = useState(false);
    const [getDistributors] = useAsyncResource(
        useGetUsers,
        group.mainGroup !== undefined
            ? { filter: { tags: [], search: group.mainGroup + '-' + group.subGroup } }
            : undefined,
        'DISTRIBUTOR,INSTALLER',
    );
    const { items: distributors } = getDistributors();
    const isDistributor = !hasPermission([Role.COORDINATOR]) && (hasPermission([Role.DISTRIBUTOR]) || hasPermission([Role.INSTALLER]));

    const { handleSubmit, reset, control, setValue, formState } = useForm<NewRequestBranch>({
        defaultValues: useMemo(() => selectedBranch, [selectedBranch]),
        mode: 'all',
    });

    const [disabledButton, setDisabledButton] = useState(false);

    const onDeleteHandler = async () => {
        const id = selectedBranch.id;
        if (id) {
            const response = await setBranchInactive(id);

            if (response !== undefined) {

                // OPTIMISTIC UPDATE: Update local state instead of clearing cache
                const mapBranch = {
                    id: id,
                    name: selectedBranch.name,
                    state: 'GREY',
                    phone: selectedBranch.phone || '',
                    latitude: selectedBranch.location.latitude,
                    longitude: selectedBranch.location.longitude,
                    city: selectedBranch.location.city,
                    assignedDistributor: (typeof selectedBranch.assignedDistributor === 'object'
                        ? selectedBranch.assignedDistributor.id
                        : selectedBranch.assignedDistributor) || 0,
                    branchAddress: selectedBranch.location.address,
                    companyName: selectedClient.companyName,
                    assignedTask: false,
                    testPeriod: false,
                    batteryPercent: 0,
                    maintenanceDays: 0,
                    nextMaintenanceDate: selectedBranch.nextMaintenanceDate || '',
                    oilPercent: 0,
                    batteryState: 'GREEN',
                    oilState: 'GREEN',
                    maintenanceState: 'GREEN',
                    groupName: selectedBranch.groupName,
                    price: 0,
                    paymentDate: '',
                };
                updateBranchLocal(mapBranch);

                toast.success(<CustomToastContent content={[t('success')]} />, toastOptionsTopRight);
                updateBranchAfterSave({ ...selectedBranch, state: 'GREY' });
            }
        }
    };

    const onRestoreHandler = async () => {
        const id = selectedBranch.id;
        if (id) {
            const response = await setBranchActive(id);

            if (response !== undefined) {

                // OPTIMISTIC UPDATE: Update local state instead of clearing cache
                const mapBranch = {
                    id: id,
                    name: selectedBranch.name,
                    state: 'GREEN',
                    phone: selectedBranch.phone || '',
                    latitude: selectedBranch.location.latitude,
                    longitude: selectedBranch.location.longitude,
                    city: selectedBranch.location.city,
                    assignedDistributor: (typeof selectedBranch.assignedDistributor === 'object'
                        ? selectedBranch.assignedDistributor.id
                        : selectedBranch.assignedDistributor) || 0,
                    branchAddress: selectedBranch.location.address,
                    companyName: selectedClient.companyName,
                    assignedTask: false,
                    testPeriod: false,
                    batteryPercent: 0,
                    maintenanceDays: 0,
                    nextMaintenanceDate: selectedBranch.nextMaintenanceDate || '',
                    oilPercent: 0,
                    batteryState: 'GREEN',
                    oilState: 'GREEN',
                    maintenanceState: 'GREEN',
                    groupName: selectedBranch.groupName,
                    price: 0,
                    paymentDate: '',
                };
                updateBranchLocal(mapBranch);

                toast.success(<CustomToastContent content={[t('success')]} />, toastOptionsTopRight);
                updateBranchAfterSave({ ...selectedBranch, state: 'GREEN' });
            }
        }
    };

    const isSelectedBranch = selectedBranch.id || !selectedClient.id ? !editable : false;

    useEffect(() => {
        setEditable(false);
        // Дистрибуторът се търси по id: в selectedBranch идва ту като User, ту като голо число.
        // Преди тук имаше Number(обект) = NaN -> setValue(undefined), и Controller-ът показваше и
        // после записваше в стойностите на формата дистрибутора на ПЪРВИЯ отворен обект (useWatch в
        // RHF 7.27 връща замразения си defaultValue от монтирането, когато стойността е undefined).
        // Ако го няма в списъка, остава този на обекта; ако няма никакъв - null, не undefined, за да не
        // се стигне до същия резерв и полето да остане празно (задължително е).
        const current = selectedBranch.assignedDistributor as User | number | null | undefined;
        const distributorId = typeof current === 'object' ? current?.id : Number(current);
        const assignedDistributor =
            distributors.find((user) => user?.id === distributorId) ??
            (current && typeof current === 'object' ? current : null);
        // Копие, не самия selectedBranch: reset() пази обекта по референция като defaultValues.
        reset({ ...selectedBranch, assignedDistributor: assignedDistributor as User });
    }, [selectedBranch]);

    const [isLoading, setIsLoading] = useState(false);

    const useSubmitHandlerAdd = async (formValue: NewRequestBranch) => {

        setDisabledButton(true);
        setIsLoading(true);
        const name = formValue.name;
        const id = editable ? selectedBranch.id : undefined;

        const response = await UseNewRequestAddBranch(
            {
                ...formValue,
                assignedDistributor: formValue.assignedDistributor!,
                isCompany: selectedClient.company,
                name: selectedClient.company ? name : selectedClient.companyName + '-' + formValue.location.address,
                id,
            },
            selectedClient.id,
        );
        let client = selectedClient;
        if (!selectedClient.company) {
            client = { ...selectedClient, city: formValue.location.city, companyAddress: formValue.location.address };
        }
        if (response !== undefined && !selectedClient.company) {
            const clientResponse = await saveClient(client);
            if (clientResponse !== undefined) updateClientAfterSave(client);
        }
        if (response !== undefined) {
            const { id } = response;
            const newBranch = { ...formValue, name: selectedClient.company ? name : selectedClient.companyName + '-' + formValue.location.address, id };

            if (selectedBranch.id) updateBranchAfterSave(newBranch);
            else onBranchSelect(newBranch);
            setEditable(false);

            // OPTIMISTIC UPDATE: Construct MapBranch and update local state
            const mapBranch = {
                id: id,
                name: selectedClient.company ? name : selectedClient.companyName,
                state: formValue.state || 'GREEN',
                phone: formValue.phone || '',
                latitude: formValue.location.latitude,
                longitude: formValue.location.longitude,
                city: formValue.location.city,
                assignedDistributor: formValue.assignedDistributor?.id || 0,
                branchAddress: formValue.location.address,
                companyName: selectedClient.companyName,
                assignedTask: false,
                testPeriod: false,
                batteryPercent: 0,
                maintenanceDays: 0,
                nextMaintenanceDate: formValue.nextMaintenanceDate || '',
                oilPercent: 0,
                batteryState: 'GREEN',
                oilState: 'GREEN',
                maintenanceState: 'GREEN',
                groupName: formValue.groupName,
                price: 0,
                paymentDate: '',
            };

            if (selectedBranch.id) {
                // Update existing branch
                updateBranchLocal(mapBranch);
                toast.success(<CustomToastContent content={[t('branchUpdate')]} />, toastOptionsTopRight);
            } else {
                // Add new branch
                addBranchLocal(mapBranch);
                toast.success(<CustomToastContent content={[t('branchSuccess')]} />, toastOptionsTopRight);
            }
        }

        // Only clear specific caches, not the branch cache
        resourceCache(useGetNewRequestBranchesByClient).clear();
        resourceCache(fetchBranchImage).clear();

        // REMOVED: resourceCache(fetchMapBranches).clear();
        // REMOVED: !selectedBranch.id && getNewBranches({ filter: {} });

        selectedClient.id && getBranches(selectedClient.id);
        setIsLoading(false);
        setDisabledButton(false);
        if (setIsNewMarkerVissible) {
            setIsNewMarkerVissible(false);
        }
    };

    useEffect(() => {
        if (!selectedClient.company) {
            setValue('phone', selectedClient.phone);
            setValue('name', selectedClient.companyName);
        }
    }, [selectedClient]);

    return (
        <>
            <Form
                id='newRequestBranch'
                className='w-100 p-0 mt-2 mb-1'
                noValidate
                onSubmit={handleSubmit(useSubmitHandlerAdd)}
            >
                <BootstrapTable className='w-100 position-relative'>
                    <thead>
                        <tr className='position-relative'>
                            <th colSpan={2} className='text-center border-top fw-bold'>
                                {t('branch')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className='d-flex flex-column'>
                        {selectedClient.company && (
                            <NewRequestTableRow label={t('name')} id='client' required={formState.errors.name}>
                                <Controller
                                    name='name'
                                    {...{ control }}
                                    rules={{ required: true }}
                                    render={({ field }) => (
                                        <Form.Control
                                            value={field.value ?? ''}
                                            onChange={({ currentTarget }) => field.onChange(currentTarget.value)}
                                            disabled={isSelectedBranch}
                                        />
                                    )}
                                />
                            </NewRequestTableRow>
                        )}
                        <NewRequestTableRow label={t('city')} id='city' required={formState.errors.location?.city}>

                            <Controller
                                name='location.city'
                                {...{ control }}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Form.Control type='text' {...field} value={field.value ?? ''} disabled={isSelectedBranch} />
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            label={t('address')}
                            id='address'
                            required={formState.errors.location?.address}
                        >
                            <Controller
                                name='location.address'
                                {...{ control }}
                                rules={{ required: true }}
                                render={({ field }) => (
                                    <Form.Control type='text' {...field} value={field.value ?? ''} disabled={isSelectedBranch} />
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            label={t('assignedDistributor')}
                            id='distributor'
                            inputContainerClass='justify-content-center'
                            required={formState.errors.assignedDistributor?.id ?? undefined}
                        >
                            <Controller
                                name='assignedDistributor'
                                {...{ control }}
                                rules={{
                                    required: {
                                        value: true,
                                        message: t('required'),
                                    },
                                    validate: (value) => value !== null,
                                }}
                                render={({ field, fieldState }) => (
                                    <>
                                        <Select
                                            isDisabled={isSelectedBranch}
                                            value={field.value}
                                            styles={optionSelectStyles}
                                            placeholder={t('assignedDistributor')}
                                            options={distributors}
                                            getOptionLabel={({ name }) => name}
                                            getOptionValue={(user: User | null) => String(user?.id) ?? ''}
                                            onChange={(user: User | null) => {
                                                field.onChange(user);
                                                setValue('groupName', user?.group.subGroup ?? '');
                                            }}
                                        />
                                        {fieldState.error && (
                                            <Form.Control.Feedback type='invalid'>
                                                {fieldState.error.message}
                                            </Form.Control.Feedback>
                                        )}
                                    </>
                                )}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow
                            label={t('groupName')}
                            id='groupName'
                            inputContainerClass='justify-content-center'
                        >
                            <Controller
                                name='groupName'
                                {...{ control }}
                                render={({ field }) => <Form.Control type='text' {...field} value={field.value ?? ''} disabled />}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow label={t('latitude')} id={'location.latitude'}>
                            <Controller
                                name='location.latitude'
                                {...{ control }}
                                render={({ field }) => <Form.Control type='text' {...field} value={field.value ?? ''} disabled />}
                            />
                        </NewRequestTableRow>
                        <NewRequestTableRow label={t('longitude')} id={'location.longitude'}>
                            <Controller
                                name='location.longitude'
                                {...{ control }}
                                render={({ field }) => <Form.Control type='text' {...field} value={field.value ?? ''} disabled />}
                            />
                        </NewRequestTableRow>
                        {selectedClient.company && (
                            <NewRequestTableRow label={t('service')} id='activity'>
                                <Controller
                                    name='activity'
                                    {...{ control }}
                                    render={({ field }) => (
                                        <Form.Control type='text' {...field} value={field.value ?? ''} disabled={isSelectedBranch} />
                                    )}
                                />
                            </NewRequestTableRow>
                        )}
                        <NewRequestTableRow label={t('imgUrl')} id={'imgUrl'}>
                            <Controller
                                name='imgUrl'
                                {...{ control }}
                                render={({ field }) => (
                                    <Form.Control
                                        type='file'
                                        placeholder='Файл'
                                        onChange={(e) => {
                                            const element = e.target as HTMLInputElement;
                                            if (element.files) {
                                                field.onChange(element.files[0]);
                                            }
                                        }}
                                        disabled={isSelectedBranch}
                                    />
                                )}
                            />
                        </NewRequestTableRow>

                        {isLoading ? (
                            <tr>
                                <td>
                                    <div className={'align-self-center '}>
                                        <Spinner animation='border' />
                                    </div>
                                </td>
                            </tr>
                        ) : (
                            <>
                                {selectedBranch.id && selectedBranch.state !== 'GREY' && (
                                    <tr className='w-100 d-flex'>
                                        {!isDistributor && (
                                            <NewRequestTableButtons
                                                formToSubmitId='newRequestBranch'
                                                onDeleteButtonClick={onDeleteHandler}
                                                toggleEdit={() => {
                                                    if (editable) reset();
                                                    setEditable((prev) => !prev);
                                                }}
                                                isEdit={editable}
                                            />
                                        )}
                                    </tr>
                                )}
                                {selectedBranch.id && selectedBranch.state === 'GREY' && (
                                    <tr className='w-100 d-flex'>
                                        {!isDistributor && (
                                            <NewRequestTableButtons
                                                formToSubmitId='newRequestBranch'
                                                onRestoreButtonClick={onRestoreHandler}
                                                toggleEdit={() => {
                                                    if (editable) reset();
                                                    setEditable((prev) => !prev);
                                                }}
                                                isEdit={editable}
                                            />
                                        )}
                                    </tr>
                                )}
                            </>
                        )}
                    </tbody>
                </BootstrapTable>
                {!selectedBranch.id &&
                    (isLoading ? null : (
                        <Button disabled={disabledButton} className='w-100' type='submit' form='newRequestBranch'>
                            {t('createBranch')}
                        </Button>
                    ))}
            </Form>
        </>
    );
};
